// Identifiers made on the device. They must be unique without asking the server, because sales are recorded
// while the internet is down.

/** Any cryptographically secure random source: WebCrypto in Node and browsers, a native module on mobile. */
export interface RandomSource {
  /** Fills the array with random bytes. */
  getRandomValues(array: Uint8Array<ArrayBuffer>): unknown;
}

const HEX = Array.from({ length: 256 }, (_, i) => i.toString(16).padStart(2, "0"));

/**
 * UUID version 7 (RFC 9562): 48 bits of Unix milliseconds, then 74 random bits. A device's sales sort by creation
 * time, and two devices cannot collide in practice. The sale id is also the sync idempotency key.
 */
export function uuidv7(nowMs: number, random: RandomSource): string {
  if (!Number.isSafeInteger(nowMs) || nowMs < 0) throw new RangeError("nowMs must be a non-negative integer");
  const b = new Uint8Array(16);
  random.getRandomValues(b);
  let t = nowMs;
  for (let i = 5; i >= 0; i--) { b[i] = t % 256; t = Math.floor(t / 256); }
  b[6] = 0x70 | (b[6]! & 0x0f);
  b[8] = 0x80 | (b[8]! & 0x3f);
  const h = Array.from(b, (x) => HEX[x]).join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

export const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
export const isUuid = (s: unknown): s is string => typeof s === "string" && UUID_RE.test(s);

/** Device code: the short prefix of every receipt number, unique per organization. */
export const DEVICE_CODE_RE = /^[A-Z0-9]{2,12}$/;

/** Human-readable receipt number: device code and the device's sequence number, e.g. "RUH01-000042". */
export function receiptNumber(deviceCode: string, deviceSeq: number): string {
  if (!DEVICE_CODE_RE.test(deviceCode)) throw new RangeError("invalid device code");
  if (!Number.isSafeInteger(deviceSeq) || deviceSeq < 1) throw new RangeError("deviceSeq must be a positive integer");
  return `${deviceCode}-${String(deviceSeq).padStart(6, "0")}`;
}
